import { BOARD_SIZE, fileOf, indexToSquare, isInCheck, rankOf } from './rules';
import type { Color, GameState, Move } from './types';

const PAPER = '#f6f2e9';
const INK = '#101010';
const MUTED = '#d9d0bc';
const ACCENT = '#c89b3c';
const DANGER = '#b23a2b';
const BLUE = '#4e7693';

const GLYPHS: Record<Color, Record<string, string>> = {
  w: { pawn: '♙', knight: '♘', bishop: '♗', rook: '♖', queen: '♕', king: '♔' },
  b: { pawn: '♟', knight: '♞', bishop: '♝', rook: '♜', queen: '♛', king: '♚' },
};

export class GameRenderer {
  readonly context: CanvasRenderingContext2D;
  width = 1;
  height = 1;
  cell = 1;
  offsetX = 0;
  offsetY = 0;
  flipped = false;
  nightMode = false;
  reducedMotion = false;
  private dpr = 1;

  constructor(readonly canvas: HTMLCanvasElement) {
    const context = canvas.getContext('2d');
    if (!context) throw new Error('Canvas 2D is unavailable');
    this.context = context;
    this.resize();
  }

  resize(): void {
    const rect = this.canvas.getBoundingClientRect();
    this.width = Math.max(1, rect.width);
    this.height = Math.max(1, rect.height);
    this.dpr = Math.min(window.devicePixelRatio || 1, 2);
    this.cell = Math.max(1, Math.floor(Math.min(this.width, this.height) / BOARD_SIZE));
    this.offsetX = Math.floor((this.width - this.cell * BOARD_SIZE) / 2);
    this.offsetY = Math.floor((this.height - this.cell * BOARD_SIZE) / 2);
    this.canvas.width = Math.round(this.width * this.dpr);
    this.canvas.height = Math.round(this.height * this.dpr);
    this.context.imageSmoothingEnabled = true;
  }

  clientToSquare(clientX: number, clientY: number): string | null {
    const rect = this.canvas.getBoundingClientRect();
    const localX = clientX - rect.left - this.offsetX;
    const localY = clientY - rect.top - this.offsetY;
    if (localX < 0 || localY < 0 || localX >= this.cell * BOARD_SIZE || localY >= this.cell * BOARD_SIZE) return null;
    const displayFile = Math.floor(localX / this.cell);
    const displayRank = Math.floor(localY / this.cell);
    return indexToSquare(this.displayToBoardIndex(displayFile, displayRank));
  }

  render(state: GameState, playerColor: Color, selected: string | null, legalMoves: Move[], thinking: boolean): void {
    const ctx = this.context;
    ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
    ctx.clearRect(0, 0, this.width, this.height);
    ctx.fillStyle = this.nightMode ? '#151713' : PAPER;
    ctx.fillRect(0, 0, this.width, this.height);

    const legalTargets = new Map<string, boolean>();
    for (const move of legalMoves) legalTargets.set(move.to, !!state.board[this.squareToIndex(move.to)]);
    const lastMove = state.lastMove;
    for (let displayRank = 0; displayRank < BOARD_SIZE; displayRank += 1) {
      for (let displayFile = 0; displayFile < BOARD_SIZE; displayFile += 1) {
        const boardIndex = this.displayToBoardIndex(displayFile, displayRank);
        const file = fileOf(boardIndex);
        const rank = rankOf(boardIndex);
        const x = this.offsetX + displayFile * this.cell;
        const y = this.offsetY + displayRank * this.cell;
        const dark = (file + rank) % 2 === 1;
        ctx.fillStyle = this.nightMode
          ? (dark ? '#34382f' : '#20231e')
          : (dark ? MUTED : PAPER);
        ctx.fillRect(x, y, this.cell, this.cell);
        if (rank <= 1 && !this.nightMode) {
          ctx.fillStyle = 'rgba(200,155,60,.10)';
          ctx.fillRect(x, y, this.cell, this.cell);
        }
        if (rank >= 6 && !this.nightMode) {
          ctx.fillStyle = 'rgba(78,118,147,.08)';
          ctx.fillRect(x, y, this.cell, this.cell);
        }
        if (lastMove && (lastMove.from === indexToSquare(boardIndex) || lastMove.to === indexToSquare(boardIndex))) {
          ctx.fillStyle = 'rgba(200,155,60,.28)';
          ctx.fillRect(x, y, this.cell, this.cell);
        }
        if (selected === indexToSquare(boardIndex)) {
          ctx.fillStyle = 'rgba(16,16,16,.22)';
          ctx.fillRect(x, y, this.cell, this.cell);
          ctx.strokeStyle = this.nightMode ? PAPER : INK;
          ctx.lineWidth = Math.max(2, this.cell * 0.06);
          ctx.strokeRect(x + 2, y + 2, this.cell - 4, this.cell - 4);
        }
        const target = legalTargets.get(indexToSquare(boardIndex));
        if (target !== undefined) {
          ctx.fillStyle = target ? 'rgba(178,58,43,.72)' : 'rgba(16,16,16,.34)';
          ctx.beginPath();
          ctx.arc(x + this.cell / 2, y + this.cell / 2, target ? this.cell * 0.32 : this.cell * 0.12, 0, Math.PI * 2);
          ctx.fill();
        }
        if (state.lastResurrection?.square === indexToSquare(boardIndex)) {
          ctx.strokeStyle = ACCENT;
          ctx.lineWidth = Math.max(2, this.cell * 0.07);
          ctx.strokeRect(x + 3, y + 3, this.cell - 6, this.cell - 6);
        }
        this.drawCoordinates(boardIndex, x, y, dark);
        const piece = state.board[boardIndex];
        if (piece) this.drawPiece(piece, x, y);
      }
    }

    const kingIndex = state.board.findIndex((piece) => piece?.color === state.turn && piece.kind === 'king');
    if (kingIndex >= 0 && isInCheck(state, state.turn)) this.drawCheckMark(kingIndex);
    if (thinking) this.drawThinkingStripe();
    void playerColor;
  }

  private displayToBoardIndex(displayFile: number, displayRank: number): number {
    const file = this.flipped ? BOARD_SIZE - 1 - displayFile : displayFile;
    const rank = this.flipped ? displayRank : BOARD_SIZE - 1 - displayRank;
    return rank * BOARD_SIZE + file;
  }

  private squareToIndex(square: string): number {
    const file = square.charCodeAt(0) - 97;
    const rank = Number(square[1]) - 1;
    return rank * BOARD_SIZE + file;
  }

  private drawCoordinates(index: number, x: number, y: number, dark: boolean): void {
    const ctx = this.context;
    const color = this.nightMode ? 'rgba(246,242,233,.54)' : (dark ? 'rgba(16,16,16,.56)' : 'rgba(16,16,16,.42)');
    ctx.fillStyle = color;
    ctx.font = `500 ${Math.max(7, Math.floor(this.cell * 0.12))}px "IBM Plex Mono"`;
    ctx.textAlign = 'left';
    ctx.textBaseline = 'top';
    if ((!this.flipped && fileOf(index) === 0) || (this.flipped && fileOf(index) === 7)) {
      ctx.fillText(String(rankOf(index) + 1), x + 3, y + 3);
    }
    ctx.textAlign = 'right';
    ctx.textBaseline = 'bottom';
    if ((!this.flipped && rankOf(index) === 0) || (this.flipped && rankOf(index) === 7)) {
      ctx.fillText(String.fromCharCode(97 + fileOf(index)), x + this.cell - 3, y + this.cell - 3);
    }
  }

  private drawPiece(piece: NonNullable<GameState['board'][number]>, x: number, y: number): void {
    const ctx = this.context;
    const glyph = GLYPHS[piece.color][piece.kind] ?? '?';
    const size = Math.max(20, Math.floor(this.cell * 0.72));
    ctx.save();
    ctx.font = `${size}px Georgia, "Times New Roman", serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.lineWidth = Math.max(1.5, this.cell * 0.025);
    ctx.strokeStyle = piece.color === 'w' ? INK : PAPER;
    ctx.fillStyle = piece.color === 'w' ? PAPER : INK;
    ctx.strokeText(glyph, x + this.cell / 2, y + this.cell / 2 + this.cell * 0.02);
    ctx.fillText(glyph, x + this.cell / 2, y + this.cell / 2 + this.cell * 0.02);
    if (piece.resurrected && !this.reducedMotion) {
      ctx.strokeStyle = ACCENT;
      ctx.lineWidth = 1;
      ctx.strokeRect(x + this.cell * 0.17, y + this.cell * 0.17, this.cell * 0.66, this.cell * 0.66);
    }
    ctx.restore();
  }

  private drawCheckMark(index: number): void {
    const display = this.boardToDisplay(index);
    const x = this.offsetX + display.file * this.cell;
    const y = this.offsetY + display.rank * this.cell;
    this.context.fillStyle = 'rgba(178,58,43,.30)';
    this.context.fillRect(x, y, this.cell, this.cell);
    this.context.strokeStyle = DANGER;
    this.context.lineWidth = Math.max(2, this.cell * 0.05);
    this.context.strokeRect(x + 2, y + 2, this.cell - 4, this.cell - 4);
  }

  private drawThinkingStripe(): void {
    const ctx = this.context;
    ctx.fillStyle = 'rgba(16,16,16,.72)';
    ctx.fillRect(this.offsetX, this.offsetY + this.cell * 7.5, this.cell * 8, this.cell * 0.5);
    ctx.fillStyle = PAPER;
    ctx.font = `500 ${Math.max(7, Math.floor(this.cell * 0.18))}px "IBM Plex Mono"`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('AI THINKING', this.offsetX + this.cell * 4, this.offsetY + this.cell * 7.75);
  }

  private boardToDisplay(index: number): { file: number; rank: number } {
    const file = fileOf(index);
    const rank = rankOf(index);
    return {
      file: this.flipped ? BOARD_SIZE - 1 - file : file,
      rank: this.flipped ? rank : BOARD_SIZE - 1 - rank,
    };
  }
}
